import { api } from '../api/client.js'
import type { MasterField, MasterScreenConfig, SelectOption } from './MasterScreen.js'

const GSTIN_HINT = '15-character GSTIN, if registered'

function loadOptionsFrom(apiPath: string, labelKey = 'name'): () => Promise<SelectOption[]> {
  return async () => {
    const result = await api.get<{ rows: Record<string, unknown>[] }>(`${apiPath}?pageSize=100`)
    return result.rows.map((r) => ({ value: String(r.id), label: String(r[labelKey] ?? r.id) }))
  }
}

// Stage 3 §5.1
export const branchesConfig: MasterScreenConfig = {
  title: 'Branch',
  apiPath: '/branches',
  searchPlaceholder: 'Search branches…',
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'code', label: 'Code' },
    { key: 'state', label: 'State' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'code', label: 'Code', type: 'text', required: true },
    { name: 'address', label: 'Address', type: 'text' },
    { name: 'state', label: 'State', type: 'text' },
    { name: 'gstin', label: 'GSTIN', type: 'text', hint: GSTIN_HINT },
  ],
}

// Stage 3 §5.6
export const vehicleTypesConfig: MasterScreenConfig = {
  title: 'Vehicle Type',
  apiPath: '/vehicle-types',
  searchPlaceholder: 'Search vehicle types…',
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'capacity_uom', label: 'UOM' },
    { key: 'capacity_value', label: 'Capacity' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Container 20ft' },
    {
      name: 'capacity_uom',
      label: 'Capacity unit',
      type: 'select',
      options: [
        { value: 'Ton', label: 'Ton' },
        { value: 'CFT', label: 'CFT' },
      ],
    },
    { name: 'capacity_value', label: 'Capacity value', type: 'number' },
  ],
}

// Stage 3 §5.4
export const chargeTypesConfig: MasterScreenConfig = {
  title: 'Charge Type',
  apiPath: '/charge-types',
  searchPlaceholder: 'Search charge types…',
  columns: [
    { key: 'name', label: 'Name' },
    {
      key: 'is_default_on_new_lr',
      label: 'Default on new LR',
      render: (r) => (r.is_default_on_new_lr ? 'Yes' : 'No'),
    },
    { key: 'sort_order', label: 'Order' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Freight' },
    { name: 'is_default_on_new_lr', label: 'Pre-fill on new LR', type: 'checkbox' },
    { name: 'sort_order', label: 'Sort order', type: 'number' },
  ],
}

// Stage 3 §5.11
export const vendorsConfig: MasterScreenConfig = {
  title: 'Vendor',
  apiPath: '/vendors',
  searchPlaceholder: 'Search vendors…',
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'gstin', label: 'GSTIN' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'gstin', label: 'GSTIN', type: 'text', hint: GSTIN_HINT },
  ],
}

// Stage 3 §5.10
export const brokersConfig: MasterScreenConfig = {
  title: 'Broker',
  apiPath: '/brokers',
  searchPlaceholder: 'Search brokers…',
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'default_brokerage_type', label: 'Brokerage type' },
    { key: 'default_brokerage_value', label: 'Value' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'gstin', label: 'GSTIN', type: 'text', hint: GSTIN_HINT },
    {
      name: 'default_brokerage_type',
      label: 'Default brokerage type',
      type: 'select',
      options: [
        { value: 'Flat', label: 'Flat amount' },
        { value: 'Percent', label: 'Percentage' },
      ],
    },
    {
      name: 'default_brokerage_value',
      label: 'Default brokerage value',
      type: 'number',
      hint: 'Above 25% needs confirmation before saving',
    },
  ],
}

// Stage 3 §5.8
export const driversConfig: MasterScreenConfig = {
  title: 'Driver',
  apiPath: '/drivers',
  searchPlaceholder: 'Search drivers…',
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'type', label: 'Type' },
    { key: 'license_expiry', label: 'Licence expiry' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'license_no', label: 'Licence number', type: 'text' },
    { name: 'license_expiry', label: 'Licence expiry', type: 'date' },
    {
      name: 'type',
      label: 'Type',
      type: 'select',
      options: [
        { value: 'Own', label: 'Own' },
        { value: 'Outsourced', label: 'Outsourced' },
      ],
    },
    {
      name: 'language_preference',
      label: 'Language',
      type: 'select',
      options: [
        { value: 'ta', label: 'தமிழ்' },
        { value: 'en', label: 'English' },
      ],
    },
  ],
}

// Stage 3 §5.7 — vehicle_type_id is loaded from the Vehicle Types master rather
// than hardcoded, since it is itself tenant-configurable.
export const vehiclesConfig: MasterScreenConfig = {
  title: 'Vehicle',
  apiPath: '/vehicles',
  searchPlaceholder: 'Search by registration number…',
  columns: [
    { key: 'registration_no', label: 'Registration No.' },
    { key: 'ownership', label: 'Ownership' },
    { key: 'status', label: 'Status' },
  ],
  fields: [
    { name: 'registration_no', label: 'Registration number', type: 'text', required: true, placeholder: 'TN 38 AB 1234' },
    {
      name: 'vehicle_type_id',
      label: 'Vehicle type',
      type: 'select',
      required: true,
      loadOptions: loadOptionsFrom('/vehicle-types'),
    },
    {
      name: 'ownership',
      label: 'Ownership',
      type: 'select',
      options: [
        { value: 'Company', label: 'Company' },
        { value: 'FinancedVendor', label: 'Financed / Vendor' },
      ],
    },
    {
      name: 'vendor_id',
      label: 'Vendor (if financed)',
      type: 'select',
      loadOptions: loadOptionsFrom('/vendors'),
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'Available', label: 'Available' },
        { value: 'OnTrip', label: 'On Trip' },
        { value: 'Maintenance', label: 'Maintenance' },
      ],
    },
  ] satisfies MasterField[],
}

// Stage 6 §3.1
export const accountingHeadsConfig: MasterScreenConfig = {
  title: 'Accounting Head',
  apiPath: '/accounting-heads',
  searchPlaceholder: 'Search accounting heads…',
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'sort_order', label: 'Order' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Vehicle Running Expenses' },
    { name: 'sort_order', label: 'Sort order', type: 'number' },
  ],
}

const TRIP_EXPENSE_CATEGORY_OPTIONS: SelectOption[] = [
  { value: 'DriverExpense', label: 'Driver Expense' },
  { value: 'Diesel', label: 'Diesel' },
  { value: 'Toll', label: 'Toll' },
  { value: 'FASTag', label: 'FASTag' },
  { value: 'Loading', label: 'Loading' },
  { value: 'Unloading', label: 'Unloading' },
  { value: 'Repair', label: 'Repair' },
  { value: 'Parking', label: 'Parking' },
  { value: 'Food', label: 'Food' },
  { value: 'Misc', label: 'Miscellaneous' },
]

// Stage 6 §3.1 — the plain, operational name staff pick when logging a petty
// cash entry; each maps to exactly one Accounting Head (§3.3).
export const expenseHeadsConfig: MasterScreenConfig = {
  title: 'Expense Head',
  apiPath: '/expense-heads',
  searchPlaceholder: 'Search expense heads…',
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'category', label: 'Category' },
    { key: 'sort_order', label: 'Order' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Diesel' },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      required: true,
      hint: 'Trip heads roll into that trip\'s profitability; Office heads never do',
      options: [
        { value: 'Trip', label: 'Trip' },
        { value: 'Office', label: 'Office' },
      ],
    },
    {
      name: 'trip_expense_category',
      label: 'Profitability bucket',
      type: 'select',
      hint: 'Required for a Trip-category head, ignored for Office',
      options: TRIP_EXPENSE_CATEGORY_OPTIONS,
    },
    {
      name: 'accounting_head_id',
      label: 'Accounting head',
      type: 'select',
      required: true,
      loadOptions: loadOptionsFrom('/accounting-heads'),
    },
    { name: 'sort_order', label: 'Sort order', type: 'number' },
  ],
}

// Stage 3 §5.9
export const customersConfig: MasterScreenConfig = {
  title: 'Customer',
  apiPath: '/customers',
  searchPlaceholder: 'Search customers…',
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'credit_limit', label: 'Credit limit' },
    { key: 'status', label: 'Status' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'gstin', label: 'GSTIN', type: 'text', hint: GSTIN_HINT },
    { name: 'billing_address', label: 'Billing address', type: 'text' },
    { name: 'credit_limit', label: 'Credit limit (₹)', type: 'number' },
    { name: 'credit_period_days', label: 'Credit period (days)', type: 'number' },
    {
      name: 'credit_block_mode',
      label: 'Credit block mode',
      type: 'select',
      options: [
        { value: 'None', label: 'None' },
        { value: 'Soft', label: 'Soft (warn + override)' },
        { value: 'Hard', label: 'Hard (block)' },
      ],
    },
    {
      name: 'requires_quotation_acceptance',
      label: 'Requires quotation acceptance',
      type: 'checkbox',
      // Matches the DB column default (Stage 2 §3.2) — without this, the Add
      // form would start unchecked and silently create every new customer with
      // the OPPOSITE of the intended default unless someone happened to tick it.
      defaultValue: true,
    },
    { name: 'allow_advance_billing', label: 'Allow advance billing (before POD)', type: 'checkbox' },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      hint: 'Blocking a customer with trips in transit will ask for confirmation',
      options: [
        { value: 'Active', label: 'Active' },
        { value: 'Blocked', label: 'Blocked' },
      ],
    },
  ],
}
